import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, MessageCircle, Phone } from "lucide-react";
import { API } from "@/App";
import { BRAND, SERVICES, waLink } from "@/data/content";
import LeadForm from "@/components/LeadForm";
import PayButton from "@/components/PayButton";
import Tools from "@/components/Tools";
import CtaBand from "@/components/CtaBand";

const DETAILS = {
  insurance: {
    h1: "Insurance Solutions in Bindki",
    intro: "Life, health, term, motor aur business insurance — sahi cover chunna utna hi zaroori hai jitna cover lena. Hum plans compare karke aapke budget aur family ke hisaab se salah dete hain.",
    points: ["Term Life Insurance — family income protection", "Health & Mediclaim — hospital expenses cover", "Motor Insurance — car, bike, commercial vehicle", "Shop & Business Insurance", "Claim assistance aur renewal support"],
    interest: ["Life Insurance", "Health Insurance", "Term Insurance"],
    note: "Documents (jaise purani policy ya ID) aap form ke saath hi upload kar sakte hain — online insurance process fast ho jata hai.",
  },
  "investment-mutual-funds": {
    h1: "Investment & Wealth Solutions in Bindki",
    intro: "SIP se shuru karein ya lumpsum invest karein — goals ke hisaab se mutual funds, ELSS tax saver, child education aur retirement planning.",
    points: ["SIP starting ₹500/month", "Lumpsum & SWP strategies", "ELSS — tax saving with growth", "Child education & retirement planning", "Live AMFI performance data se informed decisions"],
    interest: ["Mutual Fund", "Investment Planning", "Retirement Planning"],
    tools: true,
  },
  "tax-gst": {
    h1: "Tax & GST Solutions in Bindki",
    intro: "ITR filing, GST registration aur returns — deadlines ka tension chhodiye. Sahi documents, time par filing, aur expert guidance.",
    points: ["ITR filing — salaried, business, freelancer", "GST registration & monthly returns", "Accounting & bookkeeping support", "MSME / Udyam registration", "Tax notices ka jawab"],
    interest: ["Tax / ITR", "GST"],
  },
  "banking-csc": {
    h1: "Banking & Digital Services in Bindki",
    intro: "Ghar ke paas hi CSC seva — PAN card, Voter ID, Ayushman Bharat card, AePS cash withdrawal aur utility bill payments. Ab online payment ki suvidha bhi.",
    points: ["New PAN / PAN correction", "Voter ID & Ayushman Bharat card", "AePS cash withdrawal & balance enquiry", "Utility bill payments", "Post office & banking services"],
    interest: ["Banking / Post Office"],
    payments: true,
  },
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const detail = DETAILS[slug];
  const [csc, setCsc] = useState([]);

  useEffect(() => {
    if (detail?.payments) {
      axios.get(`${API}/csc-services`).then(({ data }) => setCsc(data.services.filter((s) => !["consultation", "partner-onboarding"].includes(s.key)))).catch(() => setCsc([]));
    }
  }, [slug]);

  if (!service || !detail) {
    return (
      <section className="page-hero">
        <h1>Service not found</h1>
        <p><Link to="/services" className="text-link" data-testid="service-not-found-back">View all services</Link></p>
      </section>
    );
  }

  return (
    <>
      <section className="page-hero" data-testid="service-hero">
        <div className="eyebrow gold">{service.hindi}</div>
        <h1>{detail.h1.replace(" in Bindki", "")} <span className="accent">in Bindki</span></h1>
        <p>{detail.intro}</p>
        <div className="hero-actions">
          <a href={waLink(`Namaste Utkarsh ji, mujhe ${service.title} ke baare mein jaankari chahiye.`)} target="_blank" rel="noopener noreferrer" className="btn btn-whatsapp" data-testid="service-whatsapp-cta"><MessageCircle size={16} /> WhatsApp Consultation</a>
          <a href={`tel:+91${BRAND.phone1}`} className="btn btn-primary" data-testid="service-call-cta"><Phone size={16} /> Call Now</a>
        </div>
      </section>

      <section className="section">
        <div className="detail-grid">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="eyebrow blue">What you get</div>
            <h2>{service.title} — <span className="accent">full scope.</span></h2>
            <ul className="detail-list">
              {detail.points.map((point) => <li key={point}><Check size={16} /> {point}</li>)}
            </ul>
            {detail.note && <p className="detail-note">{detail.note}</p>}
          </motion.div>
          <LeadForm preset={detail.interest} title={`${service.title} Enquiry`} />
        </div>
      </section>

      {detail.payments && (
        <section className="section section-light" data-testid="csc-services-section">
          <div className="section-head centered">
            <div className="eyebrow green">CSC services + online payment</div>
            <h2>Fee dekhkar <span className="accent">turant apply karein.</span></h2>
            <p>Razorpay se secure online payment, ya WhatsApp par baat karke office visit karein.</p>
          </div>
          <div className="csc-grid">
            {csc.map((item) => (
              <div className="csc-card" key={item.key} data-testid={`csc-card-${item.key}`}>
                <h3>{item.name}</h3>
                <strong className="mono">₹{item.fee}</strong>
                <PayButton serviceKey={item.key} label={item.name} amount={item.fee} />
              </div>
            ))}
          </div>
        </section>
      )}

      {detail.tools && <Tools />}
      <CtaBand />
    </>
  );
}
